import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { Eye, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

type SenderType = 'user' | 'admin' | 'ai' | 'system';

interface TicketMessage {
  messageId: string;
  senderId: string | null;
  senderType: string;
  content: string;
  isInternal?: boolean;
  deletedAt?: string | null;
  createdAt: string;
}

interface TicketMessageListProps {
  messages: TicketMessage[];
  isAdmin?: boolean;
  onDeleteMessage?: (messageId: string) => void;
}

const senderTypeLabels: Record<SenderType, string> = {
  user: 'You',
  admin: 'Support Agent',
  ai: 'AI Assistant',
  system: 'System',
};

const senderTypeStyles: Record<SenderType, { bubble: string; wrapper: string }> = {
  user: {
    bubble: 'bg-primary text-primary-foreground',
    wrapper: 'justify-end',
  },
  admin: {
    bubble: 'bg-muted text-foreground',
    wrapper: 'justify-start',
  },
  ai: {
    bubble: 'bg-violet-100 text-violet-900 dark:bg-violet-900 dark:text-violet-100',
    wrapper: 'justify-start',
  },
  system: {
    bubble: 'bg-transparent text-muted-foreground border border-border',
    wrapper: 'justify-center',
  },
};

function DeletedMessageContent({ message }: { message: TicketMessage }) {
  const [expanded, setExpanded] = useState(false);

  const senderKey = (
    message.senderType in senderTypeLabels ? message.senderType : 'system'
  ) as SenderType;
  const label = senderTypeLabels[senderKey];
  const isUser = senderKey === 'user';
  const isInternalNote = message.isInternal === true;

  const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const dateStr = new Date(message.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        isUser ? 'items-end' : 'items-start',
        isInternalNote && 'border-l-2 border-amber-400 pl-3',
      )}
    >
      <div className="flex items-center gap-2 px-1">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {isInternalNote && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <Eye className="h-3 w-3" />
            Internal Note
          </span>
        )}
        <span className="text-xs text-muted-foreground/60">
          {dateStr} {timestamp}
        </span>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'max-w-[75%] rounded-lg px-3 py-2 text-sm text-left cursor-pointer',
          'bg-muted/50 text-muted-foreground border border-dashed border-muted-foreground/30',
          'hover:bg-muted/80 transition-colors',
        )}
      >
        <span className="flex items-center gap-1.5 italic">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          Message was deleted
        </span>
        {expanded && (
          <div className="mt-2 pt-2 border-t border-muted-foreground/20 whitespace-pre-wrap break-words opacity-60">
            {message.content}
          </div>
        )}
      </button>
    </div>
  );
}

export function TicketMessageList({ messages, isAdmin, onDeleteMessage }: TicketMessageListProps) {
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        No messages yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {messages.map((message) => {
        const isDeleted = !!message.deletedAt;

        // Admin sees deleted messages with expand toggle; user never sees them
        if (isDeleted) {
          if (!isAdmin) return null;
          return <DeletedMessageContent key={message.messageId} message={message} />;
        }

        const senderKey = (
          message.senderType in senderTypeStyles ? message.senderType : 'system'
        ) as SenderType;
        const styles = senderTypeStyles[senderKey];
        const label = senderTypeLabels[senderKey];
        const isUser = senderKey === 'user';
        const isSystem = senderKey === 'system';

        const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        });

        const dateStr = new Date(message.createdAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        });

        if (isSystem) {
          return (
            <div key={message.messageId} className="flex justify-center">
              <div
                className={cn('max-w-md rounded-md px-3 py-1.5 text-xs text-center', styles.bubble)}
              >
                {message.content}
                <span className="ml-2 opacity-60">
                  {dateStr} {timestamp}
                </span>
              </div>
            </div>
          );
        }

        const isInternalNote = message.isInternal === true;

        return (
          <div
            key={message.messageId}
            className={cn(
              'group flex flex-col gap-1',
              isUser ? 'items-end' : 'items-start',
              isInternalNote && 'border-l-2 border-amber-400 pl-3',
            )}
          >
            <div className="flex items-center gap-2 px-1">
              <span className="text-xs font-medium text-muted-foreground">{label}</span>
              {isInternalNote && (
                <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                  <Eye className="h-3 w-3" />
                  Internal Note
                </span>
              )}
              <span className="text-xs text-muted-foreground/60">
                {dateStr} {timestamp}
              </span>
              {isAdmin && onDeleteMessage && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  onClick={() => onDeleteMessage(message.messageId)}
                  title="Unsend message"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div
              className={cn(
                'max-w-[75%] rounded-lg px-3 py-2 text-sm break-words',
                isUser && 'whitespace-pre-wrap',
                isInternalNote
                  ? 'bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800'
                  : styles.bubble,
              )}
            >
              {isUser ? (
                message.content
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
